import { useContext, useMemo } from 'react';
import { mapArticleToDisplay } from '@/condor/application/Finsight/CreditFlowResearch/Articles/ArticlesList/helpers';
import { useArticlesThemesContext } from '@/creditflowresearch/application/ArticlesAndStats/ArticlesMarketThemesContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useDictionariesContext } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import {
  transformIndustries,
  transformSectors,
} from '@/creditflowresearch/application/Dictionaries/serverSide/legacyMappers';
import type { ISector } from '@/creditflowresearch/domain/vo/ISector';
import type { IDictionaryIndustry } from '@/creditflowresearch/domain/vo/IDictionary';
import { useCurrentProduct } from './useCurrentProduct';

export const useSectorIndustryData = (row) => {
  const { themes, isInitialized } = useArticlesThemesContext();
  // Also rendered on v1 pages (issuer, deal, market overview), where v2 sectors/industries are empty.
  // mapArticleToDisplay reads the legacy `subsectorList`/`subindustryList` shape.
  const legacy = useContext(DictionariesContext);
  const { sectors: v2Sectors, industries: v2Industries } = useDictionariesContext();
  const sectors = useMemo(
    () => legacy?.sectorsListAll ?? transformSectors(v2Sectors.getAll() as ISector[]),
    [legacy?.sectorsListAll, v2Sectors],
  );
  const industries = useMemo(
    () => legacy?.industriesListAll ?? transformIndustries(v2Industries.getAll() as IDictionaryIndustry[]),
    [legacy?.industriesListAll, v2Industries],
  );
  const { productFilters, regionFilters } = usePageFiltersContext();
  const product = useCurrentProduct();

  if (!isInitialized) {
    return { isLoading: true };
  }

  const { sectorOrIndustry, subsectorOrSubindustry } = mapArticleToDisplay(
    row,
    themes,
    productFilters,
    regionFilters,
    sectors,
    industries,
    product,
  );

  return {
    sectorOrIndustry,
    subsectorOrSubindustry,
    isLoading: false,
  };
};
